import { BaseFileFiledDto } from '@diritto/common/base/base.dto';
import { Color } from '@diritto/common/style/color';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { EditCoverImage } from 'app-components/contents/editCoverImage/EditCoverImage';
import { SelectDefaultCoverImage } from 'app-components/contents/editCoverImage/SelectDefaultCoverImage';
import { ModalLayout } from 'ui-components/layout/ModalLayout';
import { isPC } from 'constants/layout';
import { ModalType } from 'constants/modal';
import { useShowModal } from 'ui-hooks/useShowModal';
import { DirittoImageEditorStyle } from 'libs/DirittoImageEditorStyle';
import React, { useCallback, useEffect, useState } from 'react';
import { ImageRatio } from '@diritto/common/base/constants';
import { ModalHandler } from 'utils/ModalHandler';
import { ViewPortHeight } from 'ui-components/layout/styles';
import { useViewPort } from 'ui-hooks/useViewPort';
import { ConfirmationMessage } from 'ui-hooks/usePreventLeave';

const MAX_IMAGE_EDIT_MODAL_WIDTH = 740;

interface CoverImageEditorModalProps {
  onSubmit: (file: BaseFileFiledDto) => void;
}

export const CoverImageEditorModal = ({ onSubmit }: CoverImageEditorModalProps) => {
  const { isShow, revealModal, showModal, hideModal } = useShowModal();

  const [showSelectPopup, setShowSelectPopup] = useState(true);
  const [selectImagePath, setSelectImagePathPath] = useState<string>();
  const { height: viewportHeight } = useViewPort();

  const onBack = useCallback(() => {
    if (window.confirm(ConfirmationMessage)) {
      setShowSelectPopup(true);
    }
  }, []);

  const handleSubmit = useCallback(
    (file: BaseFileFiledDto) => {
      setShowSelectPopup(true);
      onSubmit(file);
    },
    [onSubmit],
  );

  const handleChangeSelectImage = useCallback((imageFilePath: string) => {
    setSelectImagePathPath(imageFilePath);
    setShowSelectPopup(false);
  }, []);

  const handleCloseSelectPopup = useCallback(() => {
    hideModal();
  }, [hideModal]);

  const renderContent = useCallback(() => {
    if (showSelectPopup || !selectImagePath) {
      return <SelectDefaultCoverImage onSelected={handleChangeSelectImage} onClose={handleCloseSelectPopup} />;
    }

    return <EditCoverImage selectImagePath={selectImagePath} onSubmit={handleSubmit} onBack={onBack} />;
  }, [showSelectPopup, selectImagePath, handleSubmit, onBack, handleChangeSelectImage, handleCloseSelectPopup]);

  useEffect(() => {
    const listener = (show: boolean) => {
      if (show) {
        showModal();
      } else {
        hideModal();
      }
    };

    ModalHandler.addEventListener(ModalType.EditCover, listener);

    return () => {
      ModalHandler.removeEventListener(ModalType.EditCover);
    };
  }, [hideModal, showModal]);

  return (
    <ModalLayout isShow={isShow} revealModal={revealModal}>
      <StyledWrapper height={viewportHeight}>{renderContent()}</StyledWrapper>
    </ModalLayout>
  );
};

const StyledWrapper = styled.div(
  ({ height }: { height: number }) => css`
    width: 100vw;
    ${ViewPortHeight(height)};
    position: relative;
    overflow-y: auto;
    overscroll-behavior-y: contain;
    background-color: ${Color.White};

    @media (${isPC}) {
      width: 500px;
      height: 660px;
      max-width: ${MAX_IMAGE_EDIT_MODAL_WIDTH}px;
      max-height: ${MAX_IMAGE_EDIT_MODAL_WIDTH * ImageRatio}px;
      border-radius: 4px;
      overflow: initial;
    }

    ${DirittoImageEditorStyle};
  `,
);
